/* eslint-disable react/jsx-key */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DomainIcon from '@mui/icons-material/Domain';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WorkIcon from '@mui/icons-material/Work';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Modal,
  Tab,
  Tabs,
  TextField,
  // Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { CandidateSubmissionType, JobData } from 'components/JobQueue/utils';
import React, { useEffect, useState } from 'react';
import { getAllListData } from 'services/CommonServices';
import { SALES_ENDPOINT } from 'utils/Constant';
import { formatPhoneNumber } from 'utils/Helpers';

import CandidateCard from './CandidateCard';

interface MatchesAndShortlistProps {
  matches: CandidateSubmissionType[];
  shortlisted: CandidateSubmissionType[];
  onSelectCandidate: (candidateId: string, selected: boolean) => void;
  onExpandCandidate?: (candidateId: string) => void;
  setActiveTab: (tabIndex: number) => void;
  activeTab: number;
  onConfirmSubmit?: (
    selected: CandidateSubmissionType[],
    submitType: string,
  ) => Promise<void> | void;
  setMore: (more: boolean) => void;
  jobId: string | undefined;
  shortlist?: CandidateSubmissionType[];
  jobData?: JobData;
  Params?: any;
  matchCount?: number;
  onScoreFilterChange?: (score: string) => void;
  scoreFilterValue?: string;
  goal?: string;
}

const MatchesAndShortlist: React.FC<MatchesAndShortlistProps> = ({
  matches,
  shortlisted,
  onSelectCandidate,
  onExpandCandidate,
  activeTab,
  setActiveTab,
  onConfirmSubmit,
  setMore,
  jobId,
  shortlist,
  jobData,
  Params,
  matchCount,
  onScoreFilterChange,
  scoreFilterValue = '',
  goal,
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };
  // Track selected items from both matches and shortlist
  const [selectedItems, setSelectedItems] = React.useState<Set<string>>(
    new Set(),
  );

  const handleSelectCandidate = (candidateId: string, selected: boolean) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(candidateId);
      } else {
        next.delete(candidateId);
      }
      return next;
    });
    onSelectCandidate(candidateId, selected);
  };

  const handleExpandCandidate = (candidateId: string) => {
    if (onExpandCandidate) {
      onExpandCandidate(candidateId);
    }
  };

  const [openConfirm, setOpenConfirm] = React.useState(false);
  const [submitType, setSubmitType] = React.useState('');
  const [isSubmitProcessing, setIsSubmitProcessing] = React.useState(false);
  const [overAllMatchData, setOverAllMatchData] = useState<any>([]);
  const [openAllModal, setOpenAllModal] = useState(false);
  // Local state for input value (updates as user types)
  // Default to '60' if no score filter value is provided
  const [scoreInput, setScoreInput] = useState<string>(
    scoreFilterValue || '60',
  );
  // Resizable goal section
  const [goalSectionHeight, setGoalSectionHeight] = useState<number | 'auto'>(
    'auto',
  ); // Default height in pixels
  const [isDragging, setIsDragging] = useState(false);
  const [formulaStepsExpanded, setFormulaStepsExpanded] = useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (openAllModal && contentRef.current) {
      const height = contentRef.current.scrollHeight;
      const restrictedHeight = Math.max(100, Math.min(600, height));
      setGoalSectionHeight(restrictedHeight);
    }
  }, [goal, openAllModal]);

  // Set default score to 60 on initial load if no score filter is provided
  React.useEffect(() => {
    if (!scoreFilterValue && onScoreFilterChange) {
      onScoreFilterChange('60');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Get all selected items from either matches or shortlist based on active tab
  const selectedCandidates = React.useMemo(() => {
    const source = activeTab === 0 ? matches : shortlist;
    return (source || []).filter((c) => selectedItems.has(c.id));
  }, [activeTab, matches, shortlist, selectedItems]);

  const getMatchColor = (percentage: number) => {
    // if (percentage >= 90) return '#10b981'; // Green
    if (percentage >= 80) return '#EBF0FF'; // Blue
    // if (percentage >= 70) return '#f59e0b'; // Orange
    // return '#6b7280'; // Gray
    return '#EBF0FF';
  };
  const handleOpenConfirm = () => setOpenConfirm(true);
  const handleCloseConfirm = () => {
    setSubmitType('');
    setOpenConfirm(false);
    setIsSubmitProcessing(false);
  };
  const handleSubmitConfirm = async () => {
    if (isSubmitProcessing) return;
    setIsSubmitProcessing(true);

    try {
      if (onConfirmSubmit) {
        await onConfirmSubmit(selectedCandidates, submitType);
        handleCloseConfirm();
      }
    } catch (error) {
      console.error('Error submitting candidates:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitProcessing(false);
      setOpenConfirm(false);
      setSubmitType('');
      // Clear selections after submission (whether successful or not)
      setSelectedItems(new Set());
    }
  };

  // Resize handlers for the goal section
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const modalElement = document.querySelector('[data-goal-container-all]');
      if (!modalElement) return;

      const rect = modalElement.getBoundingClientRect();
      const newHeight = Math.max(100, Math.min(600, e.clientY - rect.top));
      setGoalSectionHeight(newHeight);
    },
    [isDragging],
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const TabsList = [
    {
      label: 'Matches',
      value: 0,
    },
    {
      label: 'Shortlisted',
      value: 1,
    },
  ];

  // Sync local input with prop value when it changes from parent
  useEffect(() => {
    setScoreInput(scoreFilterValue);
  }, [scoreFilterValue]);

  useEffect(() => {
    const scoreParam = scoreFilterValue ? `&score=${scoreFilterValue}` : '';
    getAllListData(
      `${
        SALES_ENDPOINT.MatchedCandidates
      }/${jobId}?type=matched${scoreParam}&sourceQueue=${'benchQueue'}`,
    )
      .then((res) => {
        setOverAllMatchData(res?.data || []);
      })
      .catch((err: any) => {})
      .finally(() => {});
  }, [jobId, scoreFilterValue]);

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        height: '100%',
        padding: '20px',
        paddingBottom: 'unset !important',
      }}>
      {/* Tabs */}
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '24px',
        }}>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => {
            handleTabChange(event, newValue);
          }}
          sx={{
            '& .Mui-selected': {
              color: '#000000 !important',
            },
            '& .MuiTab-root': {
              color: '#000000',
              fontFamily: 'Inter',
              fontSize: '14px',
              padding: '12px 4px',
              textTransform: 'none',
            },
            '& .MuiTabs-flexContainer': {
              gap: '12px',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#be0000',
              height: '2px',
            },
          }}>
          {TabsList.map((tab) => (
            <Tab
              sx={{
                '&.Mui-selected': {
                  color: '#000000 !important',
                },
                fontWeight: activeTab === tab.value ? 500 : 400,
                lineHeight: 'normal',
                opacity: activeTab === tab.value ? 1 : 0.6,
              }}
              key={tab.value}
              label={`${tab.label} (${
                tab.value === 0 ? matchCount ?? 0 : shortlist?.length ?? 0
              })`}
            />
          ))}
        </Tabs>

        <div style={{ alignItems: 'center', display: 'flex', gap: '12px' }}>
          {overAllMatchData && overAllMatchData?.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpenAllModal(true)}
              sx={{
                '&:hover': {
                  backgroundColor: '#f9fafb',
                  borderColor: '#9ca3af',
                },
                borderColor: '#d1d5db',
                color: '#374151',
                fontSize: '12px',
                minHeight: '28px',
                padding: '4px 8px',
                textTransform: 'none',
              }}>
              View All Scores
            </Button>
          )}
          <TextField
            value={scoreInput}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow numbers between 0-100
              if (
                value === '' ||
                (Number(value) >= 0 && Number(value) <= 100)
              ) {
                setScoreInput(value);
              }
            }}
            onBlur={() => {
              // Call API when user clicks outside
              if (onScoreFilterChange && scoreInput !== scoreFilterValue) {
                onScoreFilterChange(scoreInput);
              }
            }}
            onKeyDown={(e) => {
              // Call API when user presses Enter
              if (
                e.key === 'Enter' &&
                onScoreFilterChange &&
                scoreInput !== scoreFilterValue
              ) {
                onScoreFilterChange(scoreInput);
              }
            }}
            placeholder="Score"
            type="number"
            size="small"
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            sx={{
              '& .MuiOutlinedInput-input': {
                padding: '4px 8px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d1d5db',
              },
              // Hide number input spinner arrows
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },

              '& input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },

              '& input[type=number]::-webkit-outer-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#d1d5db',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#9ca3af',
              },
              color: '#374151',
              fontSize: '12px',
              minWidth: '80px',
              width: '80px',
            }}
          />
          <Typography
            onClick={() => setMore(true)}
            sx={{
              '&:hover': {
                color: '#242424',
              },
              alignItems: 'center',
              color: '#242424',
              cursor: 'pointer',
              display: 'flex',
              fontFamily: 'Inter',
              fontSize: '12px',
              fontWeight: 500,
              gap: '4px',
            }}>
            More
            <span style={{ fontSize: '12px' }}>›</span>
          </Typography>
        </div>
      </div>
      {/* Content */}
      <div style={{ height: 'calc(100vh - 210px)', overflowY: 'auto' }}>
        {activeTab === 0 ? (
          // Matches Tab
          <div>
            {matches?.length > 0 ? (
              matches?.map((candidate) => (
                <div key={candidate?.id} style={{ cursor: 'pointer' }}>
                  <CandidateCard
                    jobId={jobId}
                    candidate={candidate}
                    matchPercentage={candidate?.matchPercentage}
                    visaStatus={candidate?.candidate?.workStatus?.name}
                    source={candidate?.candidate?.source}
                    name={
                      `${candidate?.candidate?.firstName || ''} ${
                        candidate?.candidate?.middleName?.trim() || ''
                      } ${
                        candidate?.candidate?.lastName?.trim() || ''
                      }`.trim() || '-'
                    }
                    availability={candidate?.candidate?.benchStatus}
                    availabilityDate={candidate?.candidate?.benchAvailability}
                    email={candidate?.candidate?.email}
                    phone={
                      candidate?.candidate?.phone ||
                      candidate?.candidate?.mobileNumber
                    }
                    location={
                      `${candidate.candidate?.city?.trim() || ''}, ${
                        candidate.candidate?.state?.trim() || ''
                      }`.trim() || '-'
                    }
                    isSelected={selectedItems.has(candidate.id)}
                    topSkills={(candidate as any)?.topSkills
                      ?.map((skill) => skill)
                      .filter(Boolean)
                      .join(', ')}
                    onSelect={(selected) =>
                      handleSelectCandidate(candidate.id, selected)
                    }
                    onExpand={() => handleExpandCandidate(candidate.id)}
                    isShortList={false}
                    submittedOn={''}
                    submittedBy={''}
                    activeTab={activeTab}
                    Params={Params}
                    percentageDetail={candidate?.mathematicalBreakdown}
                    goal={goal}
                    jobTitle={jobData?.jobTitle}
                    expFrom={jobData?.expFrom}
                    expTo={jobData?.expTo}
                  />
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: '40px',
                  textAlign: 'center',
                }}>
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontFamily: 'Red Hat Display Medium',
                    fontSize: '14px',
                  }}>
                  {jobData?.candidateMatchStatus === 'inProgress'
                    ? 'Matching in Progress.'
                    : ' No matches found'}
                </Typography>
              </div>
            )}
          </div>
        ) : (
          // Shortlisted Tab
          <div>
            {shortlist?.length > 0 ? (
              shortlist.map((candidate) => (
                <div key={candidate?.id} style={{ cursor: 'pointer' }}>
                  <CandidateCard
                    jobId={jobId}
                    candidate={candidate}
                    matchPercentage={candidate.matchPercentage}
                    visaStatus={candidate?.candidate?.workStatus?.name}
                    source={candidate?.candidate?.source}
                    name={
                      `${candidate?.candidate?.firstName || ''} ${
                        candidate?.candidate?.middleName?.trim() || ''
                      } ${candidate?.candidate?.lastName?.trim() || ''}` || '-'
                    }
                    availability={candidate?.candidate?.benchStatus}
                    availabilityDate={candidate?.candidate?.benchAvailability}
                    email={candidate?.candidate?.email}
                    phone={
                      candidate?.candidate?.phone ||
                      candidate?.candidate?.mobileNumber
                    }
                    location={
                      `${candidate.candidate?.city?.trim() || ''}, ${
                        candidate.candidate?.state?.trim() || ''
                      }`.trim() || '-'
                    }
                    isSelected={(candidate as any).selectedForSubmit}
                    topSkills={(candidate as any)?.topSkills
                      ?.map((skill) => skill)
                      .filter(Boolean)
                      .join(', ')}
                    onSelect={(selected) =>
                      handleSelectCandidate(candidate.id, selected)
                    }
                    onExpand={() => handleExpandCandidate(candidate.id)}
                    isShortList={true}
                    submittedOn={candidate?.shortlistedAt}
                    submittedBy={
                      candidate?.shortlistedUser?.firstName +
                      ' ' +
                      candidate?.shortlistedUser?.lastName
                    }
                    activeTab={activeTab}
                    Params={Params}
                    goal={goal}
                    jobTitle={jobData?.jobTitle}
                    expFrom={jobData?.expFrom}
                    expTo={jobData?.expTo}
                  />
                </div>
              ))
            ) : (
              <div
                style={{
                  padding: '40px',
                  textAlign: 'center',
                }}>
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontFamily: 'Red Hat Display Medium',
                    fontSize: '14px',
                  }}>
                  No shortlisted candidates
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer submit action for Shortlisted tab */}
      {activeTab === 1 && selectedCandidates?.length > 0 && (
        <div
          style={{
            alignItems: 'center',
            background: '#ffffff',
            borderTop: '1px solid #e5e7eb',
            bottom: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            left: 0,
            padding: '12px 24px',
            position: 'sticky',
            right: 0,
          }}>
          {jobData?.sourceOfCreation !== 'manual' && (
            <Button
              variant="outlined"
              onClick={() => {
                setSubmitType('email');
                handleOpenConfirm();
              }}
              disabled={selectedCandidates?.length === 0}
              sx={{
                '&:disabled': {
                  backgroundColor: '#fff',
                  borderColor: '#E5E7EB',
                  color: '#9CA3AF',
                  opacity: 0.6,
                },
                '&:focus': {
                  boxShadow: 'none',
                  outline: 'none',
                },
                '&:hover': {
                  backgroundColor: '#fff',
                  border: '2px solid #D1D5DB',
                  borderColor: '#D1D5DB',
                  boxShadow: 'none',
                  outline: 'none',
                },
                backgroundColor: '#fff',
                border: '2px solid #D1D5DB',
                borderRadius: '6px',
                boxShadow: 'none',
                color: '#000',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontWeight: 500,
                letterSpacing: '0.28px',
                lineHeight: '20px',
                marginRight: '10px',
                minHeight: '36px',
                padding: '6px 12px',
                textTransform: 'none',
              }}>
              Download as Email
            </Button>
          )}

          <Button
            variant="contained"
            onClick={() => {
              setSubmitType('submit');
              handleOpenConfirm();
            }}
            disabled={selectedCandidates?.length === 0}
            sx={{
              '&:disabled': { opacity: 0.5 },
              '&:hover': { background: '#2d2d2d' },
              background: '#2d2d2d',
              borderRadius: '6px',
              boxShadow: 'none',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 500,
              letterSpacing: '0.28px',
              lineHeight: '20px',
              padding: '6px 12px',
              textTransform: 'none',
            }}>
            {`Mark as Submitted (${selectedCandidates?.length})`}
          </Button>
        </div>
      )}

      {/* All Scores Modal */}
      <Modal
        open={openAllModal}
        onClose={() => setOpenAllModal(false)}
        disableAutoFocus
        disableEnforceFocus>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '100vh',
            justifyContent: 'center',
            outline: 'none',
          }}>
          <Box
            sx={{
              bgcolor: '#ffffff',
              border: '1px solid #6366f1',
              borderRadius: '12px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '95vh',
              // maxWidth: '1200px',
              overflow: 'hidden',
              position: 'relative',
              width: '80%',
            }}>
            <Box
              sx={{
                alignItems: 'center',
                backgroundColor: '#ffffff',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 16px',
              }}>
              <Typography
                sx={{
                  color: '#1f2937',
                  fontSize: '18px',
                  fontWeight: 700,
                }}>
                All Matched Candidates Scores
              </Typography>
              <IconButton size="small" onClick={() => setOpenAllModal(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Resizable Goal Section */}
            <Box
              data-goal-container-all
              sx={{
                borderBottom: '1px solid #f3f4f6',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                position: 'relative',
              }}>
              {goal &&
                (() => {
                  try {
                    const goalData =
                      typeof goal === 'string' ? JSON.parse(goal) : goal;
                    const introduction = goalData?.introduction;
                    const requiredIdentity = goalData?.requiredIdentity;
                    const expFrom = jobData?.expFrom;
                    const expTo = jobData?.expTo;

                    const mustHaveSkills = goalData?.skills?.mustHave || [];
                    const primarySkills = goalData?.skills?.primary || [];
                    const secondarySkills = goalData?.skills?.secondary || [];

                    const experienceText =
                      expFrom !== null && expFrom !== undefined
                        ? `${expFrom}${
                            expTo && expTo !== expFrom && expTo !== 0
                              ? `-${expTo}`
                              : '+'
                          } years`
                        : null;

                    return (
                      <>
                        {/* Scrollable Content Area */}
                        <Box
                          ref={contentRef}
                          sx={{
                            height:
                              goalSectionHeight === 'auto'
                                ? 'auto'
                                : `${goalSectionHeight}px`,
                            overflowY: 'auto',
                            p: 2,
                          }}>
                          <Box
                            sx={{
                              borderRadius: '8px',
                              mb: 1.5,
                              p: 1.5,
                            }}>
                            {/* Job Title Header */}
                            {jobData?.jobTitle && (
                              <Box sx={{ mb: 1 }}>
                                <Typography
                                  sx={{
                                    color: '#111827',
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    mb: 0.5,
                                  }}>
                                  {jobData.jobTitle}
                                </Typography>
                                <Box
                                  sx={{
                                    alignItems: 'center',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                  }}>
                                  {requiredIdentity && (
                                    <Box
                                      sx={{
                                        backgroundColor: '#ede9fe',
                                        borderRadius: '4px',
                                        color: '#6b21a8',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        px: 1.5,
                                        py: 0.5,
                                      }}>
                                      {requiredIdentity}
                                    </Box>
                                  )}
                                  {experienceText && (
                                    <Box
                                      sx={{
                                        backgroundColor: '#f3f4f6',
                                        borderRadius: '4px',
                                        color: '#374151',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        px: 1.5,
                                        py: 0.5,
                                      }}>
                                      {experienceText}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            )}

                            {introduction && (
                              <Typography
                                sx={{
                                  color: '#374151',
                                  fontSize: '13px',
                                  fontWeight: 400,
                                  lineHeight: 1.5,
                                  mb: 1,
                                }}>
                                {introduction}
                              </Typography>
                            )}

                            {(mustHaveSkills.length > 0 ||
                              primarySkills.length > 0) && (
                              <Box sx={{ mb: 1 }}>
                                <Typography
                                  sx={{
                                    alignItems: 'center',
                                    color: '#4f46e5',
                                    display: 'flex',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    gap: 0.5,
                                    mb: 1,
                                  }}>
                                  ● Primary Skills (
                                  {mustHaveSkills.length + primarySkills.length}
                                  )
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.75,
                                  }}>
                                  {mustHaveSkills.map(
                                    (skill: string, i: number) => (
                                      <Box
                                        key={`must-${i}`}
                                        sx={{
                                          backgroundColor: '#e0e7ff',
                                          borderRadius: '4px',
                                          color: '#4338ca',
                                          fontSize: '11px',
                                          fontWeight: 500,
                                          px: 1.5,
                                          py: 0.5,
                                        }}>
                                        {skill}
                                      </Box>
                                    ),
                                  )}
                                  {primarySkills.map(
                                    (skill: string, i: number) => (
                                      <Box
                                        key={`primary-${i}`}
                                        sx={{
                                          backgroundColor: '#e0e7ff',
                                          borderRadius: '4px',
                                          color: '#4338ca',
                                          fontSize: '11px',
                                          fontWeight: 500,
                                          px: 1.5,
                                          py: 0.5,
                                        }}>
                                        {skill}
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              </Box>
                            )}

                            {/* Secondary Skills */}
                            {secondarySkills.length > 0 && (
                              <Box>
                                <Typography
                                  sx={{
                                    alignItems: 'center',
                                    color: '#6b7280',
                                    display: 'flex',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    gap: 0.5,
                                    mb: 1,
                                  }}>
                                  ○ Secondary Skills ({secondarySkills.length})
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.75,
                                  }}>
                                  {secondarySkills.map(
                                    (skill: string, i: number) => (
                                      <Box
                                        key={i}
                                        sx={{
                                          backgroundColor: '#f3f4f6',
                                          borderRadius: '4px',
                                          color: '#4b5563',
                                          fontSize: '11px',
                                          fontWeight: 500,
                                          px: 1.5,
                                          py: 0.5,
                                        }}>
                                        {skill}
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Resize Handle */}
                        <Box
                          onMouseDown={handleMouseDown}
                          sx={{
                            '&:active': {
                              backgroundColor: '#3b82f6',
                            },
                            '&:hover': {
                              backgroundColor: '#60a5fa',
                            },
                            alignItems: 'center',
                            backgroundColor: isDragging ? '#3b82f6' : '#e5e7eb',
                            cursor: 'ns-resize',
                            display: 'flex',
                            height: '5px',
                            justifyContent: 'center',
                            transition: 'background-color 0.2s ease',
                            width: '100%',
                          }}>
                          <img
                            src={require('assets/images/split-v.svg').default}
                            alt="Resize"
                            style={{
                              height: 'auto',
                              rotate: '90deg',
                              width: '24px',
                            }}
                          />
                        </Box>
                      </>
                    );
                  } catch (e) {
                    return (
                      <>
                        <Box
                          ref={contentRef}
                          sx={{
                            height:
                              goalSectionHeight === 'auto'
                                ? 'auto'
                                : `${goalSectionHeight}px`,
                            overflowY: 'auto',
                            p: 2,
                          }}>
                          <Box
                            sx={{
                              backgroundColor: '#f0fdf4',
                              border: '1px solid #86efac',
                              borderRadius: '8px',
                              mb: 1.5,
                              p: 1.5,
                            }}>
                            <Typography
                              sx={{
                                color: '#166534',
                                fontSize: '11px',
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                mb: 1,
                                textTransform: 'uppercase',
                              }}>
                              Goal
                            </Typography>
                            <Typography
                              sx={{
                                color: '#374151',
                                fontSize: '13px',
                                fontWeight: 400,
                                lineHeight: 1.6,
                              }}>
                              {typeof goal === 'string'
                                ? goal
                                : JSON.stringify(goal)}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Resize Handle */}
                        <Box
                          onMouseDown={handleMouseDown}
                          sx={{
                            alignItems: 'center',
                            cursor: 'ns-resize',
                            display: 'flex',
                            height: 'auto',
                            justifyContent: 'center',
                            padding: '4px 0',
                            width: '100%',
                          }}>
                          <img
                            src={require('assets/images/split-v.svg').default}
                            alt="Resize"
                            style={{
                              height: 'auto',
                              width: '24px',
                            }}
                          />
                        </Box>
                      </>
                    );
                  }
                })()}
            </Box>

            {/* Scrollable Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
              {overAllMatchData?.map((cand: any, index: number) => {
                const candidate = cand;
                const matchPercentage = candidate?.matchPercentage || 0;
                const percentageDetail = candidate?.mathematicalBreakdown;
                // const identityPercentage = (percentageDetail ?? []).find(
                //   (row) => row?.component === 'Identity Match',
                // );
                let matchedSkills = [];
                let secondarySkills = [];
                let primarySkillAnalysis: any = null;
                let secondarySkillAnalysis: any = null;

                (percentageDetail ?? []).map((row: any) => {
                  if (row?.component === 'Primary Skills') {
                    matchedSkills = matchedSkills.concat(
                      row?.matchedSkills ?? [],
                    );
                    primarySkillAnalysis = row?.skillAnalysis;
                  }
                  if (row?.component === 'Secondary Skills') {
                    secondarySkills = secondarySkills.concat(
                      row?.matchedSkills ?? [],
                    );
                    secondarySkillAnalysis = row?.skillAnalysis;
                  }
                });
                const name =
                  (
                    (candidate?.candidate?.firstName || '') +
                    ' ' +
                    (candidate?.candidate?.middleName?.trim() || '') +
                    ' ' +
                    (candidate?.candidate?.lastName?.trim() || '')
                  ).trim() || '-';
                return (
                  <Box
                    key={index}
                    sx={{ borderBottom: '1px solid #e5e7eb', mb: 4, pb: 4 }}>
                    {/* Header: Name and Match Score */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 3,
                        position: 'relative',
                      }}>
                      <Box
                        sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
                        <Typography
                          sx={{
                            color: '#1f2937',
                            fontSize: '20px',
                            fontWeight: 700,
                          }}>
                          {name}
                        </Typography>
                        {/* Rank Badge */}
                        {candidate?.rank && (
                          <Box
                            sx={{
                              backgroundColor: '#4f46e5',
                              borderRadius: '4px',
                              color: '#ffffff',
                              fontSize: '12px',
                              fontWeight: 800,
                              padding: '2px 8px',
                            }}>
                            #{candidate?.rank}
                          </Box>
                        )}
                        {(candidate?.candidate?.city ||
                          candidate?.candidate?.state ||
                          candidate?.candidate?.overallExperience ||
                          candidate?.candidate?.workStatus?.name) && (
                          <Box
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                              gap: 2,
                              mt: 1,
                            }}>
                            {(candidate?.candidate?.city ||
                              candidate?.candidate?.state) && (
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  gap: 0.5,
                                }}>
                                <LocationOnIcon
                                  sx={{ color: '#ef4444', fontSize: '16px' }}
                                />
                                <Typography
                                  sx={{ color: '#6b7280', fontSize: '13px' }}>
                                  {candidate?.candidate?.city || ''}
                                  {candidate?.candidate?.city &&
                                  candidate?.candidate?.state
                                    ? ', '
                                    : ''}
                                  {candidate?.candidate?.state}
                                </Typography>
                              </Box>
                            )}
                            {candidate?.candidate?.overallExperience && (
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  gap: 0.5,
                                }}>
                                <WorkIcon
                                  sx={{ color: '#92400e', fontSize: '16px' }}
                                />
                                <Typography
                                  sx={{ color: '#6b7280', fontSize: '13px' }}>
                                  {candidate?.candidate?.overallExperience}{' '}
                                  Years
                                </Typography>
                              </Box>
                            )}
                            {candidate?.candidate?.workStatus?.name && (
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  gap: 0.5,
                                }}>
                                <RocketLaunchIcon
                                  sx={{ color: '#ef4444', fontSize: '16px' }}
                                />
                                <Typography
                                  sx={{ color: '#6b7280', fontSize: '13px' }}>
                                  {candidate?.candidate?.workStatus?.name}
                                </Typography>
                              </Box>
                            )}
                            {candidate?.candidate?.identity && (
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  gap: 0.5,
                                }}>
                                <DomainIcon
                                  sx={{ color: '#6b7280', fontSize: '16px' }}
                                />
                                <Typography
                                  sx={{
                                    color: '#6b7280',
                                    fontSize: '12px',
                                    fontWeight: 400,
                                  }}>
                                  {candidate?.candidate?.identity}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography
                          sx={{
                            color: '#9ca3af',
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                          }}>
                          Match Score
                        </Typography>
                        <Typography
                          sx={{
                            color: '#10b981',
                            fontSize: '32px',
                            fontWeight: 900,
                            lineHeight: 1,
                          }}>
                          {matchPercentage}%
                        </Typography>
                      </Box>
                    </Box>

                    {/* Score Breakdown - Grid Layout */}
                    {Array.isArray(percentageDetail) &&
                      percentageDetail?.length > 0 && (
                        <Box
                          sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            mb: 4,
                          }}>
                          {percentageDetail.map((row: any, idx: number) => {
                            if (
                              row?.identityClass === 'identity-match' ||
                              row?.component === 'Secondary Skills' ||
                              row?.component === 'Identity Match'
                            )
                              return null;

                            const score = parseFloat(row?.score) || 0;
                            const max = parseFloat(row?.maxPercentage) || 0;
                            const percentage = Math.min(
                              max > 0 ? (score / max) * 100 : 0,
                              100,
                            );
                            const colors = [
                              '#a855f7',
                              '#06b6d4',
                              '#f59e0b',
                              '#10b981',
                              '#6366f1',
                            ];
                            const color = colors[idx % colors.length];

                            const formattedScore =
                              score % 1 === 0
                                ? score.toString()
                                : score.toFixed(1);
                            const formattedMax = Math.round(max).toString();
                            return (
                              <Box key={idx}>
                                <Typography
                                  sx={{
                                    color: '#6b7280',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '10px',
                                    fontWeight: 500,
                                    letterSpacing: '0.5px',
                                    mb: 0.5,
                                    textTransform: 'uppercase',
                                  }}>
                                  {row?.component || ''}
                                </Typography>
                                <Box
                                  sx={{
                                    bgcolor: '#e5e7eb',
                                    borderRadius: '2px',
                                    height: '6px',
                                    mb: 0.5,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    width: '100%',
                                  }}>
                                  <Box
                                    sx={{
                                      bgcolor: color,
                                      height: '100%',
                                      transition: 'width 0.3s ease',
                                      width: `${percentage}%`,
                                    }}
                                  />
                                </Box>
                                <Typography
                                  sx={{
                                    color: '#111827',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    textAlign: 'right',
                                  }}>
                                  {formattedScore}/{formattedMax}
                                </Typography>
                              </Box>
                            );
                          })}

                          {/* Identity Match */}
                          {/* {identityPercentage && (
                            <Box sx={{ gridColumn: 'span 3' }}>
                              <Typography
                                sx={{
                                  color: '#6b7280',
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '10px',
                                  fontWeight: 500,
                                  letterSpacing: '0.5px',
                                  mb: 0.5,
                                  textTransform: 'uppercase',
                                }}>
                                {identityPercentage?.component}
                              </Typography>
                              <Box
                                sx={{
                                  bgcolor: (() => {
                                    const status =
                                      identityPercentage?.identityStatus?.toLowerCase();
                                    if (status?.includes('exact match'))
                                      return '#10b981';
                                    if (status?.includes('related role'))
                                      return '#f59e0b';
                                    if (status?.includes('role mismatch'))
                                      return '#ef4444';
                                    return '#10b981';
                                  })(),
                                  borderRadius: '2px',
                                  height: '6px',
                                  mb: 0.5,
                                  width: '100%',
                                }}
                              />
                              <Typography
                                sx={{
                                  color: (() => {
                                    const status =
                                      identityPercentage?.identityStatus?.toLowerCase();
                                    if (status?.includes('exact match'))
                                      return '#047857';
                                    if (status?.includes('related role'))
                                      return '#b45309';
                                    if (status?.includes('role mismatch'))
                                      return '#dc2626';
                                    return '#047857';
                                  })(),
                                  fontFamily: 'Inter, sans-serif',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  textAlign: 'right',
                                }}>
                                ✓ {identityPercentage?.identityStatus} (
                                {identityPercentage?.identityPercentage}%)
                              </Typography>
                            </Box>
                          )} */}
                        </Box>
                      )}

                    {/* AI Summary Text */}
                    {candidate?.justification && (
                      <Box
                        sx={{
                          bgcolor: '#f9fafb',
                          borderRadius: '8px',
                          mb: 3,
                          p: 2,
                        }}>
                        <Typography
                          sx={{
                            color: '#374151',
                            fontSize: '14px',
                            fontStyle: 'italic',
                            lineHeight: 1.6,
                          }}>
                          {String(candidate?.justification)}
                        </Typography>
                      </Box>
                    )}

                    {/* Role/Title Match Analysis */}
                    {(() => {
                      const roleTitleMatch = (percentageDetail ?? []).find(
                        (row: any) => row?.component === 'Role/Title Match',
                      );
                      if (!roleTitleMatch) return null;

                      const roleHistory = roleTitleMatch?.roleHistory || [];
                      const analysis = roleTitleMatch?.roleTitleAnalysis || {};
                      const formulas = analysis?.formulas || {};

                      return (
                        <Box
                          sx={{
                            borderTop: '1px solid #e5e7eb',
                            mb: 4,
                            pt: 4,
                          }}>
                          {/* Section Header */}
                          <Typography
                            sx={{
                              color: '#374151',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              mb: 1.5,
                            }}>
                            Role/Title score calculation
                          </Typography>

                          {/* Score Card with Pink Gradient Background */}
                          <Box
                            sx={{
                              background:
                                'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
                              border: '1px solid #f9a8d4',
                              borderRadius: '8px',
                              mb: 2,
                              p: '12px 16px',
                            }}>
                            {/* Score Display */}
                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                sx={{
                                  color: '#374151',
                                  fontSize: '0.9rem',
                                  fontWeight: 600,
                                }}>
                                Score:{' '}
                                <Typography
                                  component="span"
                                  sx={{
                                    color: (() => {
                                      const score =
                                        analysis.calculatedScore ||
                                        roleTitleMatch?.score ||
                                        0;
                                      const maxScore =
                                        analysis.maxScore ||
                                        roleTitleMatch?.maxPercentage ||
                                        35;
                                      const percentage =
                                        (score / maxScore) * 100;

                                      if (percentage >= 85) return '#16a34a'; // Green
                                      if (percentage >= 70) return '#eab308'; // Yellow
                                      if (percentage >= 50) return '#f97316'; // Orange
                                      return '#dc2626'; // Red
                                    })(),
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                  }}>
                                  {analysis.calculatedScore ||
                                    roleTitleMatch?.score}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    color: '#9ca3af',
                                    fontSize: '0.9rem',
                                    fontWeight: 400,
                                  }}>
                                  {' '}
                                  /{' '}
                                  {analysis.maxScore ||
                                    roleTitleMatch?.maxPercentage}{' '}
                                  points
                                </Typography>
                              </Typography>
                            </Box>

                            {/* Calculation */}
                            {analysis.calculationSteps &&
                              analysis.calculationSteps.length > 0 && (
                                <Box
                                  sx={{
                                    bgcolor: 'rgba(236, 72, 153, 0.08)',
                                    borderLeft: '3px solid #ec4899',
                                    borderRadius: '4px',
                                    mb: 1,
                                    overflow: 'hidden',
                                    p: 1.5,
                                  }}>
                                  <Typography
                                    sx={{
                                      color: '#4b5563',
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                      mb: 1.25,
                                    }}>
                                    Calculation:
                                  </Typography>
                                  {analysis.calculationSteps.map(
                                    (step: any, index: number) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          alignItems: 'center',
                                          borderBottom:
                                            index <
                                            analysis.calculationSteps.length - 1
                                              ? '1px solid rgba(255, 255, 255, 0.6)'
                                              : 'none',
                                          display: 'flex',
                                          pb:
                                            index <
                                            analysis.calculationSteps.length - 1
                                              ? 1.25
                                              : 0,
                                          pt: index > 0 ? 1.25 : 0,
                                        }}>
                                        <Box
                                          sx={{
                                            bgcolor: 'rgba(236, 72, 153, 0.3)',
                                            borderRadius: '3px',
                                            color: '#831843',
                                            flexShrink: 0,
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            mr: 1.5,
                                            px: 1.25,
                                            py: 0.5,
                                          }}>
                                          Step {step.step}
                                        </Box>
                                        <Typography
                                          sx={{
                                            color: '#374151',
                                            flex: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.8,
                                          }}>
                                          {step.value}
                                        </Typography>
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              )}

                            {/* Aggregation Details */}
                            <Box
                              sx={{
                                borderTop: '1px dashed #e5e7eb',
                                mt: 1,
                                pt: 1,
                              }}>
                              <Typography
                                sx={{
                                  color: '#6b7280',
                                  fontSize: '0.8rem',
                                  lineHeight: 1.5,
                                }}>
                                Aggregation: {analysis.aggregationMethod};
                                Trajectory: {analysis.trajectory}; T_adj ={' '}
                                {analysis.trajectory === 'ascending'
                                  ? '+0.05'
                                  : analysis.trajectory === 'descending'
                                  ? '-0.05'
                                  : '0'}
                                ; {roleHistory.length}(roles) evaluated; S_agg:{' '}
                                {analysis.s_agg}; W_current:{' '}
                                {analysis.w_current}; W_best:{' '}
                                {analysis.w_best_historical}
                              </Typography>
                            </Box>

                            {/* Formula Steps - Collapsible */}
                            {Object.keys(formulas).length > 0 && (
                              <Box sx={{ mt: 1.5 }}>
                                <Box
                                  onClick={() =>
                                    setFormulaStepsExpanded(
                                      !formulaStepsExpanded,
                                    )
                                  }
                                  sx={{
                                    '&:hover': {
                                      opacity: 0.8,
                                    },
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                  }}>
                                  <Typography
                                    sx={{
                                      alignItems: 'center',
                                      color: '#4b5563',
                                      display: 'flex',
                                      fontSize: '0.85rem',
                                      fontWeight: 600,
                                      gap: 0.5,
                                    }}>
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: 'inline-block',
                                        fontSize: '14px',
                                        transform: formulaStepsExpanded
                                          ? 'rotate(90deg)'
                                          : 'rotate(0deg)',
                                        transition: 'transform 0.2s',
                                      }}>
                                      ›
                                    </Typography>
                                    Formula steps
                                  </Typography>
                                </Box>

                                {formulaStepsExpanded && (
                                  <Box
                                    sx={{
                                      background: 'rgba(255, 255, 255, 0.6)',
                                      borderRadius: '6px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '6px',
                                      mt: 1,
                                      p: '10px',
                                    }}>
                                    {Object.entries(formulas).map(
                                      ([key, value]: [string, any]) => (
                                        <Box key={key}>
                                          <Typography
                                            sx={{
                                              color: '#4b5563',
                                              fontSize: '0.8rem',
                                            }}>
                                            {/* <Typography
                                              component="span"
                                              sx={{
                                                color: '#831843',
                                                fontFamily: 'monospace',
                                                fontWeight: 500,
                                                mr: 0.75,
                                                textTransform: 'capitalize',
                                              }}>
                                              {key
                                                .replace(/_/g, ' ')
                                                .replace(
                                                  /^step(\d+) /,
                                                  'Step$1 ',
                                                )}
                                              :
                                            </Typography> */}
                                            {String(value)}
                                          </Typography>
                                        </Box>
                                      ),
                                    )}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>

                          {/* Role History Table */}
                          {roleHistory.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography
                                sx={{
                                  color: '#374151',
                                  fontSize: '0.85rem',
                                  fontWeight: 600,
                                  mb: 1,
                                }}>
                                Role History (weighted)
                              </Typography>
                              <Box
                                sx={{
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                }}>
                                <Box
                                  component="table"
                                  sx={{
                                    borderCollapse: 'collapse',
                                    fontSize: '0.8rem',
                                    width: '100%',
                                  }}>
                                  <Box component="thead">
                                    <Box component="tr">
                                      <Box
                                        component="th"
                                        sx={{
                                          background:
                                            'rgba(236, 72, 153, 0.12)',
                                          border: '1px solid #e5e7eb',
                                          color: '#831843',
                                          fontWeight: 600,
                                          p: '6px 10px',
                                          textAlign: 'left',
                                        }}>
                                        Title
                                      </Box>
                                      <Box
                                        component="th"
                                        sx={{
                                          background:
                                            'rgba(236, 72, 153, 0.12)',
                                          border: '1px solid #e5e7eb',
                                          color: '#831843',
                                          fontWeight: 600,
                                          p: '6px 10px',
                                          textAlign: 'left',
                                          width: '60px',
                                        }}>
                                        Rank
                                      </Box>
                                      <Box
                                        component="th"
                                        sx={{
                                          background:
                                            'rgba(236, 72, 153, 0.12)',
                                          border: '1px solid #e5e7eb',
                                          color: '#831843',
                                          fontWeight: 600,
                                          p: '6px 10px',
                                          textAlign: 'left',
                                          width: '70px',
                                        }}>
                                        S_base
                                      </Box>
                                      <Box
                                        component="th"
                                        sx={{
                                          background:
                                            'rgba(236, 72, 153, 0.12)',
                                          border: '1px solid #e5e7eb',
                                          color: '#831843',
                                          fontWeight: 600,
                                          p: '6px 10px',
                                          textAlign: 'left',
                                          width: '80px',
                                        }}>
                                        W_role
                                      </Box>
                                      <Box
                                        component="th"
                                        sx={{
                                          background:
                                            'rgba(236, 72, 153, 0.12)',
                                          border: '1px solid #e5e7eb',
                                          color: '#831843',
                                          fontWeight: 600,
                                          p: '6px 10px',
                                          textAlign: 'left',
                                        }}>
                                        W_role_calculation
                                      </Box>
                                      <Box
                                        component="th"
                                        sx={{
                                          background:
                                            'rgba(236, 72, 153, 0.12)',
                                          border: '1px solid #e5e7eb',
                                          color: '#831843',
                                          fontWeight: 600,
                                          p: '6px 10px',
                                          textAlign: 'left',
                                        }}>
                                        Tenure_calculation
                                      </Box>
                                      <Box
                                        component="th"
                                        sx={{
                                          background:
                                            'rgba(236, 72, 153, 0.12)',
                                          border: '1px solid #e5e7eb',
                                          color: '#831843',
                                          fontWeight: 600,
                                          p: '6px 10px',
                                          textAlign: 'left',
                                        }}>
                                        Reason
                                      </Box>
                                    </Box>
                                  </Box>
                                  <Box component="tbody">
                                    {roleHistory.map(
                                      (role: any, index: number) => (
                                        <Box
                                          key={index}
                                          component="tr"
                                          sx={{
                                            '&:nth-of-type(even)': {
                                              background:
                                                'rgba(255, 255, 255, 0.6)',
                                            },
                                          }}>
                                          <Box
                                            component="td"
                                            sx={{
                                              border: '1px solid #e5e7eb',
                                              color: '#374151',
                                              p: '6px 10px',
                                            }}>
                                            {role.designation}
                                          </Box>
                                          <Box
                                            component="td"
                                            sx={{
                                              border: '1px solid #e5e7eb',
                                              color: '#6b7280',
                                              p: '6px 10px',
                                            }}>
                                            {role.rank}
                                          </Box>
                                          <Box
                                            component="td"
                                            sx={{
                                              border: '1px solid #e5e7eb',
                                              color: '#6b7280',
                                              p: '6px 10px',
                                            }}>
                                            {role.s_base}
                                          </Box>
                                          <Box
                                            component="td"
                                            sx={{
                                              border: '1px solid #e5e7eb',
                                              color: '#6b7280',
                                              p: '6px 10px',
                                            }}>
                                            {role.w_role?.toFixed(2)}
                                          </Box>
                                          <Box
                                            component="td"
                                            sx={{
                                              border: '1px solid #e5e7eb',
                                              p: '6px 10px',
                                            }}>
                                            <Box
                                              sx={{
                                                background:
                                                  'rgba(251, 207, 232, 0.3)',
                                                borderRadius: '4px',
                                                color: '#831843',
                                                display: 'inline-block',
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem',
                                                px: 1,
                                                py: 0.5,
                                              }}>
                                              {role.w_role_calculation}
                                            </Box>
                                          </Box>
                                          <Box
                                            component="td"
                                            sx={{
                                              border: '1px solid #e5e7eb',
                                              p: '6px 10px',
                                            }}>
                                            <Box
                                              sx={{
                                                background:
                                                  'rgba(251, 207, 232, 0.3)',
                                                borderRadius: '4px',
                                                color: '#831843',
                                                display: 'inline-block',
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem',
                                                px: 1,
                                                py: 0.5,
                                              }}>
                                              {role.tenure_calculation || 'N/A'}
                                            </Box>
                                          </Box>
                                          <Box
                                            component="td"
                                            sx={{
                                              border: '1px solid #e5e7eb',
                                              color: '#6b7280',
                                              p: '6px 10px',
                                            }}>
                                            {role.reason}
                                          </Box>
                                        </Box>
                                      ),
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}

                    {/* Strengths Section */}
                    {/* {Array.isArray(candidate?.strengths) &&
                      candidate?.strengths?.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            sx={{
                              alignItems: 'center',
                              color: '#4b5563',
                              display: 'flex',
                              fontSize: '13px',
                              fontWeight: 700,
                              gap: 0.5,
                              mb: 1.5,
                            }}>
                            ✓ Strengths
                          </Typography>
                          <Box
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {candidate?.strengths?.map((s, i) => (
                              <Box
                                key={i}
                                sx={{
                                  bgcolor: '#dcfce7',
                                  border: '1px solid #bbf7d0',
                                  borderRadius: '4px',
                                  color: '#166534',
                                  fontSize: '11px',
                                  px: 1.5,
                                  py: 0.5,
                                }}>
                                ✓ {s}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )} */}

                    {/* Considerations Section */}
                    {/* {Array.isArray(candidate?.gapsConcerns) &&
                      candidate?.gapsConcerns?.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography
                            sx={{
                              alignItems: 'center',
                              color: '#4b5563',
                              display: 'flex',
                              fontSize: '13px',
                              fontWeight: 700,
                              gap: 0.5,
                              mb: 1.5,
                            }}>
                            ⚠ Considerations
                          </Typography>
                          <Box
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {candidate?.gapsConcerns?.map((c, i) => (
                              <Box
                                key={i}
                                sx={{
                                  bgcolor: '#fee2e2',
                                  border: '1px solid #fecaca',
                                  borderRadius: '4px',
                                  color: '#991b1b',
                                  fontSize: '11px',
                                  px: 1.5,
                                  py: 0.5,
                                }}>
                                ⚠ {c}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )} */}

                    <Box sx={{ borderTop: '1px solid #e5e7eb', mb: 3, pt: 4 }}>
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 3,
                        }}>
                        <Typography
                          sx={{
                            color: '#111827',
                            fontSize: '13px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                          }}>
                          SKILL ANALYSIS
                        </Typography>
                        <Box
                          sx={{
                            alignItems: 'center',
                            display: 'flex',
                            gap: 1,
                          }}>
                          <CheckCircleIcon
                            sx={{ color: '#10b981', fontSize: '16px' }}
                          />
                          <Typography
                            sx={{
                              color: '#374151',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '12px',
                              fontWeight: 500,
                            }}>
                            {matchedSkills?.length} Primary Skill
                          </Typography>
                        </Box>
                      </Box>

                      {(primarySkillAnalysis || secondarySkillAnalysis) && (
                        <Box
                          sx={{ borderTop: '1px solid #e5e7eb', mb: 3, pt: 4 }}>
                          {/* Primary Skills Row */}
                          {primarySkillAnalysis && (
                            <Box sx={{ mb: 3 }}>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  bgcolor: '#ecfdf5',
                                  borderLeft: '4px solid #10b981',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  gap: 2,
                                  mb: 2,
                                  px: 2,
                                  py: 1.5,
                                }}>
                                <Typography
                                  sx={{
                                    color: '#065f46',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    minWidth: '120px',
                                  }}>
                                  Primary Skills:
                                </Typography>
                                <Typography
                                  sx={{
                                    color: '#374151',
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: '12px',
                                    letterSpacing: '-0.3px',
                                  }}>
                                  {primarySkillAnalysis?.calculation}
                                </Typography>
                              </Box>
                            </Box>
                          )}

                          {/* Secondary Skills Row */}
                          {secondarySkills.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Box
                                sx={{
                                  alignItems: 'center',
                                  bgcolor: '#faf5ff',
                                  borderLeft: '4px solid #8b5cf6',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  gap: 2,
                                  px: 2,
                                  py: 1.5,
                                }}>
                                <Typography
                                  sx={{
                                    color: '#6b21a8',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    minWidth: '120px',
                                  }}>
                                  Secondary Skills:
                                </Typography>
                                <Typography
                                  sx={{
                                    color: '#374151',
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: '12px',
                                    letterSpacing: '-0.3px',
                                  }}>
                                  {secondarySkillAnalysis?.calculation}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Primary Matched Skills */}
                      {Array.isArray(matchedSkills) &&
                        matchedSkills?.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography
                              sx={{
                                alignItems: 'center',
                                color: '#10b981',
                                display: 'flex',
                                fontSize: '11px',
                                fontWeight: 600,
                                gap: 0.5,
                                mb: 1.5,
                                textTransform: 'uppercase',
                              }}>
                              <CheckCircleIcon
                                sx={{ color: '#10b981', fontSize: '14px' }}
                              />{' '}
                              PRIMARY SKILLS MATCHED ({matchedSkills?.length})
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                              }}>
                              {matchedSkills?.map((s, i) => (
                                <Chip
                                  key={i}
                                  label={
                                    <>
                                      {s?.matchType === 'direct_primary'
                                        ? '✓✓'
                                        : s?.matchType === 'direct_secondary'
                                        ? '✓'
                                        : s?.matchType === 'ecosystem_primary'
                                        ? '≈'
                                        : s?.matchType === 'ecosystem_secondary'
                                        ? '~'
                                        : ''}{' '}
                                      {[
                                        'ecosystem_primary',
                                        'ecosystem_secondary',
                                      ].includes(s?.matchType) ? (
                                        <>
                                          <strong>{String(s?.skill)}</strong> →{' '}
                                          {String(s?.matchedWith)}{' '}
                                          {s?.score ? `(${s.score})` : ''}
                                        </>
                                      ) : (
                                        <>
                                          {s?.matchedWith}{' '}
                                          {s?.score ? `(${s.score})` : ''}
                                        </>
                                      )}
                                    </>
                                  }
                                  size="small"
                                  sx={{
                                    bgcolor:
                                      s?.score === 0.7
                                        ? 'rgba(99,102,241,0.1)'
                                        : '#dcfce7',
                                    borderRadius: '6px',
                                    color:
                                      s?.score === 0.7 ? '#6366f1' : '#15803d',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                      {/* Secondary Skills */}
                      {Array.isArray(secondarySkills) &&
                        secondarySkills?.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            <Typography
                              sx={{
                                alignItems: 'center',
                                color: '#15803d',
                                display: 'flex',
                                fontSize: '13px',
                                fontWeight: 700,
                                gap: 0.5,
                                mb: 1.5,
                              }}>
                              ✓ Secondary Matched Skills (
                              {secondarySkills?.length})
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                              }}>
                              {secondarySkills?.map((s, i) => (
                                // <Tooltip
                                //   key={i}
                                //   title={s.matchType}
                                //   arrow
                                //   placement="bottom"
                                //   classes={{
                                //     arrow: 'job-header-tooltip-arrow',
                                //     tooltip: 'job-header-tooltip',
                                //   }}>
                                <Box
                                  sx={{
                                    bgcolor:
                                      s?.score === 0.7
                                        ? 'rgba(99,102,241,0.1)'
                                        : '#f0fdf4',
                                    border:
                                      s?.score === 0.7
                                        ? '1px solid #c7d2fe'
                                        : '1px solid #dcfce7',
                                    borderRadius: '4px',
                                    color:
                                      s?.score === 0.7 ? '#6366f1' : '#15803d',
                                    fontSize: '11px',
                                    px: 1.5,
                                    py: 0.5,
                                  }}>
                                  {s?.matchType === 'direct_primary'
                                    ? '✓✓'
                                    : s?.matchType === 'direct_secondary'
                                    ? '✓'
                                    : s?.matchType === 'ecosystem_primary'
                                    ? '≈'
                                    : s?.matchType === 'ecosystem_secondary'
                                    ? '~'
                                    : ''}{' '}
                                  {[
                                    'ecosystem_primary',
                                    'ecosystem_secondary',
                                  ].includes(s?.matchType) ? (
                                    <span>
                                      <span
                                        style={{
                                          fontWeight: 'bold',
                                        }}>
                                        {`${String(s?.skill)}`}
                                      </span>{' '}
                                      {'→'}{' '}
                                      <span>{`${String(s?.matchedWith)}`}</span>{' '}
                                      {s?.score ? `(${s.score})` : ''}
                                    </span>
                                  ) : (
                                    <span>
                                      {s?.matchedWith}{' '}
                                      {s?.score ? `(${s.score})` : ''}
                                    </span>
                                  )}
                                </Box>
                                // </Tooltip>
                              ))}
                            </Box>
                          </Box>
                        )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Confirmation Dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        open={openConfirm}
        onClose={handleCloseConfirm}
        PaperProps={{
          style: {
            borderRadius: '12px',
          },
        }}>
        <DialogTitle
          sx={{
            color: '#111827',
            fontFamily: 'Red Hat Display SemiBold',
            fontSize: '18px',
          }}>
          {`Submit Candidates (${selectedCandidates?.length} Selected)`}
          <Typography
            sx={{
              color: '#6b7280',
              fontFamily: 'Red Hat Display Medium',
              fontSize: '14px',
              marginBottom: '12px',
            }}>
            Review and confirm the candidates you want to submit.
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseConfirm}
            sx={{
              '&:hover': {
                backgroundColor: '#f3f4f6',
              },
              color: '#6b7280',
              position: 'absolute',
              right: 8,
              top: 8,
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ padding: isSmDown ? '12px 16px' : '16px 24px' }}>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {selectedCandidates.map((c) => (
              <div
                key={c.id}
                style={{
                  backgroundColor: '#FAFAFA',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: isSmDown ? '10px' : '12px',
                }}>
                {/* Top Row - Checkbox, Match Percentage, Tags */}
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}>
                  <div
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: '10px',
                    }}>
                    {/* Match Percentage Circle */}
                    {c.matchPercentage && (
                      <div
                        style={{
                          alignItems: 'center',
                          backgroundColor: getMatchColor(c.matchPercentage),
                          border: '1px solid #7995E8',
                          borderRadius: '9999px',
                          color: '#2453C3',
                          display: 'flex',
                          fontFamily: 'Red Hat Display semibold',
                          fontSize: '12px',
                          fontWeight: '600',
                          justifyContent: 'center',
                          minHeight: '22px',
                          minWidth: '40px',
                          padding: '4px',
                        }}>
                        {c.matchPercentage}%
                      </div>
                    )}

                    {/* Tags */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {c.candidate.workStatus?.name && (
                        <Chip
                          label={c.candidate.workStatus?.name}
                          size="small"
                          sx={{
                            backgroundColor: '#F0F0F0',
                            border: '1px solid #DBDBDB',
                            color: '#7C7C7C',
                            fontFamily: 'Red Hat Display semibold',
                            fontSize: '12px',
                            fontWeight: '500',
                            height: '20px',
                            padding: '4px',
                          }}
                        />
                      )}
                      {c.candidate?.source && (
                        <Chip
                          label={c.candidate?.source}
                          size="small"
                          sx={{
                            backgroundColor: '#FFE3E3',
                            border: '1px solid #E87979',
                            color: '#C32424',
                            fontFamily: 'Red Hat Display semibold',
                            fontSize: '12px',
                            fontWeight: '500',
                            height: '20px',
                            padding: '4px',
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Candidate Name and Availability Row */}
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '8px',
                  }}>
                  <Checkbox
                    checked={selectedItems.has(c.id)}
                    onChange={(e) =>
                      handleSelectCandidate(c.id, e.target.checked)
                    }
                    sx={{
                      '&.Mui-checked': { color: '#616161' },
                      color: '#979797 !important',
                      padding: '4px',
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      {/* Candidate Name */}
                      <Typography
                        sx={{
                          color: '#242424',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                          fontWeight: 600,
                          lineHeight: '20px',
                        }}>
                        {`${c?.candidate?.firstName || ''} ${
                          c?.candidate?.middleName || ''
                        } ${c?.candidate?.lastName || ''}`}
                      </Typography>

                      {/* Availability */}
                      <div
                        style={{
                          alignItems: 'center',
                          display: 'flex',
                        }}>
                        <CheckCircleIcon
                          sx={{
                            color: '#0E700E',
                            fontFamily: 'Red Hat Display semibold',
                            fontSize: '12px',
                            fontWeight: '600',
                            marginRight: '6px',
                          }}
                        />
                        <Typography
                          sx={{
                            color: '#0E700E',
                            fontFamily: 'Inter',
                            fontSize: '10px',
                            fontWeight: 600,
                            lineHeight: '14px',
                            marginRight: '6px',
                          }}>
                          {c?.candidate?.benchStatus}
                        </Typography>
                        <span
                          style={{
                            backgroundColor: '#0E700E',
                            borderRadius: '100%',
                            height: '4px',
                            marginRight: '6px',
                            width: '4px',
                          }}
                        />
                        <Typography
                          sx={{
                            color: '#0E700E',
                            fontFamily: 'Inter',
                            fontSize: '10px',
                            fontWeight: 600,
                            lineHeight: '14px',
                            marginRight: '6px',
                          }}>
                          {c?.candidate?.benchAvailability}
                        </Typography>
                      </div>
                    </div>
                    {/* Contact Information */}
                    <div
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: '8px',
                        // marginBottom: isExpanded ? '16px' : '0',
                      }}>
                      <Typography
                        sx={{
                          color: '#616161',
                          fontFamily: 'Inter',
                          fontSize: '12px',
                          fontWeight: 400,
                          lineHeight: '14px',
                          marginBottom: '2px',
                        }}>
                        {c?.candidate?.email}
                      </Typography>
                      <span
                        style={{
                          backgroundColor: '#D9D9D9',
                          borderRadius: '100%',
                          height: '4px',
                          width: '4px',
                        }}
                      />
                      <Typography
                        sx={{
                          color: '#616161',
                          fontFamily: 'Inter',
                          fontSize: '12px',
                          fontWeight: 400,
                          lineHeight: '14px',
                          marginBottom: '2px',
                        }}>
                        {c?.candidate?.phone
                          ? formatPhoneNumber(c?.candidate?.phone)
                          : c?.candidate?.mobileNumber
                          ? formatPhoneNumber(c?.candidate?.mobileNumber)
                          : ''}
                      </Typography>
                      <span
                        style={{
                          backgroundColor: '#D9D9D9',
                          borderRadius: '100%',
                          height: '4px',
                          width: '4px',
                        }}
                      />
                      <Typography
                        sx={{
                          color: '#616161',
                          fontFamily: 'Red Hat Display Medium',
                          fontSize: '13px',
                        }}>
                        {(c?.candidate?.city || c?.candidate?.state) && (
                          <span>
                            {[
                              c?.candidate?.city,
                              c?.candidate?.state,
                              // c?.candidate?.country,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </span>
                        )}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: isSmDown ? '10px 16px' : '12px 24px' }}>
          <Button
            onClick={handleCloseConfirm}
            variant="outlined"
            sx={{
              '&:hover': { backgroundColor: '#FFFFFF', borderColor: '#D1D5DB' },
              backgroundColor: '#FFFFFF',
              border: '1px solid',
              borderColor: '#E5E7EB',
              color: '#6b7280',
              textTransform: 'none',
            }}>
            Cancel
          </Button>
          {selectedCandidates?.length > 0 && (
            <Button
              disabled={isSubmitProcessing}
              onClick={handleSubmitConfirm}
              startIcon={
                isSubmitProcessing ? (
                  <CircularProgress size={18} sx={{ color: '#ffffff' }} />
                ) : null
              }
              variant="contained"
              sx={{
                '&.Mui-disabled': {
                  background: '#111827',
                  color: '#ffffff',
                  opacity: 0.7,
                },
                '&:hover': { background: '#111827' },
                background: '#111827',
                borderRadius: '8px',
                minWidth: '140px',
                textTransform: 'none',
              }}>
              {submitType === 'email' ? 'Download and Submit' : 'Proceed'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MatchesAndShortlist;
